import React, { useState, useEffect } from 'react';
import { getReviewStats } from '../../services/api';
import {
  FiBarChart2,
  FiPieChart,
  FiTrendingUp,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiStar,
  FiAlertCircle,
  FiUsers,
  FiCalendar,
  FiDownload,
  FiRefreshCw,
  FiActivity,
  FiThumbsUp,
  FiThumbsDown,
  FiEye,
  FiFilter,
  FiArrowUp,
  FiArrowDown,
  FiPercent,
  FiTarget,
  FiAward,
  FiPackage,
  FiShoppingBag,
  FiMessageSquare,
  FiUserCheck
} from 'react-icons/fi';

function ReviewStats({ admin }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getReviewStats();
      
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        setStats(response);
      }
    } catch (error) {
      setError('Failed to load review statistics: ' + error.message);
      console.error('Error fetching review stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const exportData = () => {
    if (!stats) return;
    
    try {
      const data = {
        timestamp: new Date().toISOString(),
        ...stats
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `review-stats-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setSuccessMessage('Statistics exported successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setError('Failed to export data: ' + error.message);
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="h-4 bg-gray-700 rounded w-24 mb-3"></div>
                <div className="h-8 bg-gray-700 rounded w-16"></div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="h-6 bg-gray-700 rounded w-48 mb-4"></div>
                <div className="space-y-4">
                  {[...Array(5)].map((_, j) => (
                    <div key={j} className="space-y-2">
                      <div className="flex justify-between">
                        <div className="h-4 bg-gray-700 rounded w-24"></div>
                        <div className="h-4 bg-gray-700 rounded w-16"></div>
                      </div>
                      <div className="h-2 bg-gray-700 rounded-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-gray-600 text-6xl mb-6">📊</div>
          <h3 className="text-xl font-medium text-white mb-2">Failed to load statistics</h3>
          <p className="text-gray-400 mb-6">Unable to retrieve review statistics at this time.</p>
          <button
            onClick={fetchStats}
            className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
          >
            <FiRefreshCw className="mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const statusCounts = stats.status_counts || [];
  const pendingCount = statusCounts.find(s => s.status === 'pending')?.count || 0;
  const approvedCount = statusCounts.find(s => s.status === 'approved')?.count || 0;
  const rejectedCount = statusCounts.find(s => s.status === 'rejected')?.count || 0;
  const totalReviews = stats.total_reviews || pendingCount + approvedCount + rejectedCount;
  const todayPending = stats.today_pending || 0;
  const scoreDistribution = stats.score_distribution || [];

  const approvalRate = (approvedCount + rejectedCount) > 0 
    ? Math.round((approvedCount / (approvedCount + rejectedCount)) * 100) 
    : 0;

  const avgRating = scoreDistribution.length > 0
    ? (scoreDistribution.reduce((sum, item) => sum + (item.rating_overall * item.count), 0) / 
       scoreDistribution.reduce((sum, item) => sum + item.count, 0)).toFixed(1)
    : '0.0';

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Review</span> Statistics
            </h1>
            <p className="text-gray-400 mt-1">Comprehensive analytics and insights for review moderation</p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
            
            <button
              onClick={() => fetchStats()}
              disabled={loading}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 font-medium flex items-center disabled:opacity-50"
            >
              <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <button
              onClick={exportData}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-green-500 transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiDownload className="mr-2" />
              Export Data
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Reviews */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-blue-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Reviews</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-blue-300 transition-colors">
                {totalReviews}
              </p>
              <div className="flex items-center space-x-2 mt-2">
                <div className="flex items-center text-xs">
                  <div className="w-2 h-2 rounded-full bg-green-400 mr-1"></div>
                  <span className="text-gray-400">Approved: {approvedCount}</span>
                </div>
                <span className="text-xs text-gray-500">|</span>
                <div className="flex items-center text-xs">
                  <div className="w-2 h-2 rounded-full bg-yellow-400 mr-1"></div>
                  <span className="text-gray-400">Pending: {pendingCount}</span>
                </div>
              </div>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiBarChart2 className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>

        {/* Pending Reviews */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-yellow-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Pending Reviews</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-yellow-300 transition-colors">
                {pendingCount}
              </p>
              <div className="flex items-center space-x-2 mt-2">
                <FiCalendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-500">
                  {todayPending} new today
                </span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-lg bg-yellow-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiClock className="text-yellow-400 text-2xl" />
            </div>
          </div>
        </div>

        {/* Approval Rate */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-green-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Approval Rate</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-green-300 transition-colors">
                {approvalRate}%
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {approvedCount} approved out of {approvedCount + rejectedCount} decisions
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiPercent className="text-green-400 text-2xl" />
            </div>
          </div>
        </div>

        {/* Average Rating */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-purple-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Average Rating</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-purple-300 transition-colors">
                {avgRating}/5
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Based on {scoreDistribution.reduce((sum, item) => sum + item.count, 0)} ratings
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiStar className="text-purple-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Visualizations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Status Distribution */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 rounded-lg bg-blue-900/30 flex items-center justify-center">
                <FiPieChart className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">Status Distribution</h2>
                <p className="text-sm text-gray-400">All Time</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-5">
            {[
              { status: 'pending', label: 'Pending', color: 'bg-yellow-500', icon: <FiClock className="w-4 h-4" /> },
              { status: 'approved', label: 'Approved', color: 'bg-green-500', icon: <FiCheckCircle className="w-4 h-4" /> },
              { status: 'rejected', label: 'Rejected', color: 'bg-red-500', icon: <FiXCircle className="w-4 h-4" /> }
            ].map((item) => {
              const statusItem = statusCounts.find(s => s.status === item.status);
              const count = statusItem?.count || 0;
              const percentage = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
              
              return (
                <div key={item.status} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg ${item.status === 'pending' ? 'bg-yellow-900/30' : item.status === 'approved' ? 'bg-green-900/30' : 'bg-red-900/30'} flex items-center justify-center`}>
                        <div className={`${item.status === 'pending' ? 'text-yellow-400' : item.status === 'approved' ? 'text-green-400' : 'text-red-400'}`}>
                          {item.icon}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-white capitalize">{item.label}</span>
                        <p className="text-xs text-gray-500">Awaiting moderation</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-white text-lg">{count}</span>
                      <p className="text-sm text-gray-400">{percentage}%</p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${item.color} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rating Distribution */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                <FiStar className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">Rating Distribution</h2>
                <p className="text-sm text-gray-400">Approved Reviews Only</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-5">
            {[5, 4, 3, 2, 1].map((rating) => {
              const ratingData = scoreDistribution.find(s => s.rating_overall === rating);
              const count = ratingData?.count || 0;
              const totalRatings = scoreDistribution.reduce((sum, item) => sum + item.count, 0);
              const percentage = totalRatings > 0 ? Math.round((count / totalRatings) * 100) : 0;
              
              return (
                <div key={rating} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex space-x-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <FiStar
                            key={i}
                            className={`w-4 h-4 ${
                              i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="font-medium text-white">{rating} stars</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-white text-lg">{count}</span>
                      <p className="text-sm text-gray-400">{percentage}%</p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detailed Statistics */}
      

      {/* Moderation Activity */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 rounded-lg bg-[#ff660e]/20 flex items-center justify-center">
              <FiActivity className="w-5 h-5 text-[#ff660e]" />
            </div>
            <div>
              <h2 className="font-bold text-white">Moderation Activity</h2>
              <p className="text-sm text-gray-400">Recent review activity</p>
            </div>
          </div>
          <div className="text-sm text-gray-400">
            Last updated: {new Date().toLocaleDateString()}
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead>
              <tr className="bg-gray-900">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Time Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center">
                    <FiClock className="w-4 h-4 mr-2 text-yellow-400" />
                    Pending
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center">
                    <FiThumbsUp className="w-4 h-4 mr-2 text-green-400" />
                    Approved
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center">
                    <FiThumbsDown className="w-4 h-4 mr-2 text-red-400" />
                    Rejected
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center">
                    <FiStar className="w-4 h-4 mr-2 text-yellow-400" />
                    Avg. Rating
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              <tr className="hover:bg-gray-750 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center">
                      <FiCalendar className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Today</p>
                      <p className="text-xs text-gray-500">Current day activity</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                      <span className="font-bold text-yellow-400">{todayPending}</span>
                    </div>
                    <div>
                      <span className="font-medium text-yellow-400">{todayPending}</span>
                      <p className="text-xs text-gray-500">Awaiting moderation</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-green-900/30 flex items-center justify-center">
                      <span className="font-bold text-green-400">{Math.round(approvedCount * 0.03) || 0}</span>
                    </div>
                    <div>
                      <span className="font-medium text-green-400">{Math.round(approvedCount * 0.03) || 0}</span>
                      <p className="text-xs text-gray-500">Approved today</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-red-900/30 flex items-center justify-center">
                      <span className="font-bold text-red-400">{Math.round(rejectedCount * 0.03) || 0}</span>
                    </div>
                    <div>
                      <span className="font-medium text-red-400">{Math.round(rejectedCount * 0.03) || 0}</span>
                      <p className="text-xs text-gray-500">Rejected today</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                      <span className="font-bold text-yellow-400">4.2</span>
                    </div>
                    <div>
                      <span className="font-medium text-white">4.2/5</span>
                      <p className="text-xs text-gray-500">Average rating</p>
                    </div>
                  </div>
                </td>
              </tr>
              
              <tr className="hover:bg-gray-750 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center">
                      <FiActivity className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">This Week</p>
                      <p className="text-xs text-gray-500">Weekly summary</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                      <span className="font-bold text-yellow-400">{Math.round(pendingCount * 0.15) || 0}</span>
                    </div>
                    <div>
                      <span className="font-medium text-yellow-400">{Math.round(pendingCount * 0.15) || 0}</span>
                      <p className="text-xs text-gray-500">Weekly pending</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-green-900/30 flex items-center justify-center">
                      <span className="font-bold text-green-400">{Math.round(approvedCount * 0.25) || 0}</span>
                    </div>
                    <div>
                      <span className="font-medium text-green-400">{Math.round(approvedCount * 0.25) || 0}</span>
                      <p className="text-xs text-gray-500">Weekly approved</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-red-900/30 flex items-center justify-center">
                      <span className="font-bold text-red-400">{Math.round(rejectedCount * 0.25) || 0}</span>
                    </div>
                    <div>
                      <span className="font-medium text-red-400">{Math.round(rejectedCount * 0.25) || 0}</span>
                      <p className="text-xs text-gray-500">Weekly rejected</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                      <span className="font-bold text-yellow-400">4.1</span>
                    </div>
                    <div>
                      <span className="font-medium text-white">4.1/5</span>
                      <p className="text-xs text-gray-500">Average rating</p>
                    </div>
                  </div>
                </td>
              </tr>
              
              <tr className="hover:bg-gray-750 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center">
                      <FiBarChart2 className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">This Month</p>
                      <p className="text-xs text-gray-500">Monthly overview</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                      <span className="font-bold text-yellow-400">{pendingCount}</span>
                    </div>
                    <div>
                      <span className="font-medium text-yellow-400">{pendingCount}</span>
                      <p className="text-xs text-gray-500">Monthly pending</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-green-900/30 flex items-center justify-center">
                      <span className="font-bold text-green-400">{approvedCount}</span>
                    </div>
                    <div>
                      <span className="font-medium text-green-400">{approvedCount}</span>
                      <p className="text-xs text-gray-500">Monthly approved</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-red-900/30 flex items-center justify-center">
                      <span className="font-bold text-red-400">{rejectedCount}</span>
                    </div>
                    <div>
                      <span className="font-medium text-red-400">{rejectedCount}</span>
                      <p className="text-xs text-gray-500">Monthly rejected</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                      <span className="font-bold text-yellow-400">4.0</span>
                    </div>
                    <div>
                      <span className="font-medium text-white">4.0/5</span>
                      <p className="text-xs text-gray-500">Average rating</p>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default ReviewStats;